// ─── Types ─────────────────────────────────────────────────────────────────────
export type ArticleMeta = {
  id: number;
  slug: string;
  title: string;
  seoTitle?: string;
  excerpt?: string;
  status?: 'draft' | 'archived' | 'maintenance' | 'published';
  image?: string;
  imageAlt?: string;
  author: string;
  authorBio?: string;
  authorImage?: string;
  date: string;
  readTime?: string;
  categories: string[];
  tags: string[];
  featured?: boolean;
  relatedArticles?: number[];
  metaDescription?: string;
  __dir: string;
};

export type Article = ArticleMeta & {
  content: string;         // markdown body
};

// ─── Eager meta + lazy body globs ──────────────────────────────────────────────
const metaModules = import.meta.glob("/src/content/articles/**/meta.json", {
  eager: true,
  import: "default",
}) as Record<string, Omit<ArticleMeta, "__dir">>;

const bodyModules = import.meta.glob("/src/content/articles/**/body.md", {
  eager: true,
  import: "default",
  query: "?raw",
}) as Record<string, string>;

// ─── Build runtime manifest & indexes ──────────────────────────────────────────
const summaries: ArticleMeta[] = Object.entries(metaModules)
  .map(([path, meta]) => {
    const dir = path.replace(/\/meta\.json$/i, "");
    return { ...meta, __dir: dir } as ArticleMeta;
  })
  .filter((m) => m && m.id && m.slug && m.title)
  .filter((m) => {
    const status = m.status ?? 'published';
    return status !== 'archived' && status !== 'maintenance';
  })
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

const byId = new Map<number, ArticleMeta>();
const bySlug = new Map<string, ArticleMeta>();
for (const m of summaries) {
  byId.set(m.id, m);
  bySlug.set(m.slug, m);
}

export const allArticles = summaries;

const articleCacheById = new Map<number, Article>();
const articleCacheBySlug = new Map<string, Article>();

for (const meta of summaries) {
  const key = `${meta.__dir}/body.md`;
  const content = bodyModules[key as keyof typeof bodyModules];

  if (!content) {
    console.warn("[articles] body.md missing for", key);
    continue;
  }

  const article: Article = { ...meta, content };
  articleCacheById.set(meta.id, article);
  articleCacheBySlug.set(meta.slug, article);
}

// ─── Public API ────────────────────────────────────────────────────────────────
export function getAllSummaries(): ArticleMeta[] {
  return summaries;
}

export function getArticleByIdSync(id: number): ArticleMeta | undefined {
  return byId.get(id);
}

export function getArticleBySlugSync(slug: string): ArticleMeta | undefined {
  return bySlug.get(slug);
}

export function getArticleByIdStatic(id: number): Article | undefined {
  return articleCacheById.get(id);
}

export function getArticleBySlugStatic(slug: string): Article | undefined {
  return articleCacheBySlug.get(slug);
}

export async function getArticleById(id: number): Promise<Article | undefined> {
  return getArticleByIdStatic(id);
}

export async function getArticleBySlug(slug: string): Promise<Article | undefined> {
  return getArticleBySlugStatic(slug);
}

export function getRelatedArticlesByIds(relatedIds: number[]): ArticleMeta[] {
  return relatedIds
    .map(id => byId.get(id))
    .filter((article): article is ArticleMeta => article !== undefined)
    .slice(0, 3);
}
